import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';
import React, { Component, type ErrorInfo, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { HEXIA_SURVEY_RUNTIME_VERSION, inspectSurveyCapabilities, type CompatibilityDiagnostic, type JsonObject } from '@hexia/survey-client/core';
import { GetSurveyFromMissionAssignationViewModel, MyMissionClient, SurveyFormModel } from '../../../api/api.g';
import useApi from '../../../hooks/api-hook';
import { useAppSelector } from '../../../hooks/store-hook';
import Loading from '../Loading';
import HexiaNativeSurvey from './HexiaNativeSurvey';
import SurveyJsFallback from './SurveyJsFallback';
import { getSurveyRuntimeSelection, type SurveyClientRuntime } from './surveyRuntimeSelection';
import { reportSurveyFallback, startSurveyFallbackRetry } from './surveyFallbackReporter';
import { restoreSurveyDrafts } from './surveyDraftCache';
import SurveyLoadError from './SurveyLoadErrorScreen';
import { getSurveyLoadErrorKey, isSurveyCheckInRequired, isSurveyOfflineError } from './surveyLoadFailure';
import useTranslation from '../../../hooks/translation-hook';
import { withTimeout } from '../../../helpers/withTimeout';

type GateProps = {
  route: {
    name?: string;
    params?: { missionAssignationId?: string };
  };
};

type CachedSession = {
  runtime: SurveyClientRuntime;
  survey: unknown;
};

class NativeSurveyBoundary extends Component<{
  children: ReactNode;
  fallback: ReactNode;
  onError(error: Error, info: ErrorInfo): void;
}, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() { return { failed: true }; }

  componentDidCatch(error: Error, info: ErrorInfo) { this.props.onError(error, info); }

  render() { return this.state.failed ? this.props.fallback : this.props.children; }
}

const cacheKey = (missionAssignationId: string) => `hexia:survey-session:${missionAssignationId}`;

const surveyPlatform = Platform.OS === 'web'
  ? 'web'
  : Platform.OS === 'ios'
    ? 'ios'
    : 'android';

const parseForm = (form: SurveyFormModel): { json?: JsonObject; diagnostics: CompatibilityDiagnostic[] } => {
  try {
    const json = JSON.parse(form.formJson ?? '{}') as JsonObject;
    const report = inspectSurveyCapabilities(json, surveyPlatform);
    return { json, diagnostics: [...report.diagnostics] };
  } catch {
    return {
      diagnostics: [{
        code: 'runtime.invalid-form-json',
        feature: 'invalid FormJson',
        severity: 'fallback',
      }],
    };
  }
};

const formsOf = (first?: SurveyFormModel): SurveyFormModel[] => {
  const forms: SurveyFormModel[] = [];
  let current = first;
  while (current?.formVersionId) {
    forms.push(current);
    current = current.nextForm;
  }
  return forms;
};

const SurveyRuntimeGate = (props: GateProps) => {
  const navigation = useNavigation();
  const t = useTranslation();
  const missionAssignationId = props.route.params?.missionAssignationId;
  const authStore = useAppSelector((selector) => selector.auth);
  const api = useApi(MyMissionClient);
  const [runtime, setRuntime] = useState<SurveyClientRuntime>();
  const [survey, setSurvey] = useState<GetSurveyFromMissionAssignationViewModel>();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>();
  const [retrySeed, setRetrySeed] = useState(0);
  const active = useRef(true);
  const fallbackSessionKey = useRef(`survey-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const forms = useMemo(() => formsOf(survey?.survey), [survey]);
  const inspected = useMemo(() => forms.map((form) => ({ form, ...parseForm(form) })), [forms]);
  const parsedForms = useMemo(() => inspected.map((item) => item.json!), [inspected]);

  useEffect(() => {
    active.current = true;
    const stopRetry = startSurveyFallbackRetry();
    return () => {
      active.current = false;
      stopRetry();
    };
  }, []);

  useEffect(() => {
    if (!missionAssignationId) {
      setRuntime('surveyjs');
      setLoading(false);
      return;
    }

    let cancelled = false;
    const initialize = async () => {
      setLoading(true);
      setLoadError(undefined);
      const selectedRuntime = await withTimeout(getSurveyRuntimeSelection(), 20000)
        .catch(() => 'surveyjs' as SurveyClientRuntime);
      if (cancelled || !active.current) return;
      setRuntime(selectedRuntime);

      if (selectedRuntime !== 'hexia') {
        setLoading(false);
        return;
      }

      try {
        const result = await withTimeout(api.getMissionSurvey(missionAssignationId, api.tokenSource?.token), 20000);
        if (cancelled || !active.current) return;
        await restoreSurveyDrafts(missionAssignationId, result);
        setSurvey(result);
        await AsyncStorage.setItem(cacheKey(missionAssignationId), JSON.stringify({
          runtime: selectedRuntime,
          survey: result.toJSON(),
        } satisfies CachedSession)).catch(() => undefined);
      } catch (error) {
        if (cancelled || !active.current) return;
        // Offline recovery must not bypass a server-side configuration, access,
        // or mission-state rejection with a stale cached questionnaire.
        if (isSurveyOfflineError(error)) {
          try {
            const cachedText = await AsyncStorage.getItem(cacheKey(missionAssignationId));
            const cached = cachedText ? JSON.parse(cachedText) as CachedSession : undefined;
            if (cached?.runtime === selectedRuntime) {
              const cachedSurvey = GetSurveyFromMissionAssignationViewModel.fromJS(cached.survey);
              await restoreSurveyDrafts(missionAssignationId, cachedSurvey);
              if (!cancelled && active.current) {
                setSurvey(cachedSurvey);
                setLoading(false);
              }
              return;
            }
          } catch {
            // A corrupt/unavailable cache must still end in an actionable error.
          }
        }
        if (!cancelled && active.current) {
          setLoadError(error || new Error('Survey load failed'));
          if (isSurveyCheckInRequired(error)) {
            navigation.navigate({ name: 'mymissions', params: { id: missionAssignationId } } as never);
          }
        }
      }

      if (!cancelled && active.current) setLoading(false);
    };

    if (authStore.idToken) void initialize();
    return () => { cancelled = true; };
  }, [api, authStore.idToken, missionAssignationId, retrySeed, navigation]);

  if (loadError) return <SurveyLoadError
    message={t(getSurveyLoadErrorKey(loadError)!)}
    onRetry={() => setRetrySeed((seed) => seed + 1)}
    onBack={() => {
      if (Platform.OS === 'web' && typeof window !== 'undefined' && (window as any).ReactNativeWebView?.postMessage) {
        (window as any).ReactNativeWebView.postMessage('back');
      } else {
        navigation.navigate({ name: 'mymissions', params: { id: missionAssignationId } } as never);
      }
    }}
  />;
  if (loading || !runtime) return <Loading />;
  if (runtime !== 'hexia' || !survey) return <SurveyJsFallback {...props} />;

  const incompatible = inspected.filter((item) => item.diagnostics.some((diagnostic) => diagnostic.severity === 'fallback'));

  if (incompatible.length) {
    incompatible.forEach(({ form, diagnostics }) => {
      void reportSurveyFallback({
        product: 'mystery-shopping',
        formVersionId: form.formVersionId,
        clientVersion: Constants.expoConfig?.version ?? 'unknown',
        runtimeVersion: HEXIA_SURVEY_RUNTIME_VERSION,
        platform: surveyPlatform,
        sessionKey: fallbackSessionKey.current,
        diagnostics,
      });
    });
    return <SurveyJsFallback {...props} />;
  }

  return <NativeSurveyBoundary
    fallback={<SurveyJsFallback {...props} />}
    onError={() => {
      const formVersionId = forms[0]?.formVersionId;
      if (!formVersionId) return;
      void reportSurveyFallback({
        product: 'mystery-shopping',
        formVersionId,
        clientVersion: Constants.expoConfig?.version ?? 'unknown',
        runtimeVersion: HEXIA_SURVEY_RUNTIME_VERSION,
        platform: surveyPlatform,
        sessionKey: fallbackSessionKey.current,
        diagnostics: [{ code: 'runtime.initialization-failed', feature: 'runtime initialization' }],
      });
    }}
  >
    <HexiaNativeSurvey {...props} survey={survey} parsedForms={parsedForms} />
  </NativeSurveyBoundary>;
};

export default SurveyRuntimeGate;
